import React, { useEffect, useMemo, useState } from 'react';
import VoloceanLoader from '../loading/VoloceanLoader';
import { useAuth } from '../../contexts/AuthContext';
import { StandaloneStorageMode, normalizeStandaloneCodeInput } from '../../config/standaloneAccess';
import { isAdminRole } from '../../utils/authRoles';

interface StandaloneCodeGateProps {
  children: React.ReactNode;
  accessCode: string;
  storageKey: string;
  storageMode?: StandaloneStorageMode;
  pageLabel?: string;
}

const getStorage = (storageMode: StandaloneStorageMode): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return storageMode === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
};

export const StandaloneCodeGate: React.FC<StandaloneCodeGateProps> = ({
  children,
  accessCode,
  storageKey,
  storageMode = 'local',
  pageLabel = 'this page',
}) => {
  const { user, loading } = useAuth();
  const [codeInput, setCodeInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [storageReady, setStorageReady] = useState(false);

  const isAdmin = isAdminRole(user?.role);
  const storage = useMemo(() => getStorage(storageMode), [storageMode]);
  const expectedCode = useMemo(() => normalizeStandaloneCodeInput(accessCode), [accessCode]);
  const gateConfigured = expectedCode.length > 0;

  useEffect(() => {
    if (!storage) {
      setIsUnlocked(false);
      setStorageReady(true);
      return;
    }

    try {
      setIsUnlocked(storage.getItem(storageKey) === 'true');
    } catch {
      setIsUnlocked(false);
    } finally {
      setStorageReady(true);
    }
  }, [storage, storageKey]);

  const unlockPage = () => {
    if (storage) {
      try {
        storage.setItem(storageKey, 'true');
      } catch {
        setIsUnlocked(true);
        return;
      }
    }
    setIsUnlocked(true);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');

    if (!gateConfigured) {
      setErrorMessage('Access is not configured for this page. Contact VOLOCEAN if you need entry.');
      return;
    }

    const entered = normalizeStandaloneCodeInput(codeInput);
    if (!entered) {
      setErrorMessage('Enter the access code.');
      return;
    }

    if (entered === expectedCode) {
      unlockPage();
      return;
    }

    setErrorMessage('That code does not match. Check for spaces or typos and try again.');
  };

  if (loading || !storageReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-950">
        <VoloceanLoader size="lg" message="Verifying access..." />
      </div>
    );
  }

  if (isAdmin) {
    return <>{children}</>;
  }

  if (!gateConfigured) {
    return (
      <div className="min-h-screen bg-primary-950 text-white px-4 py-12 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto mt-8 sm:mt-16">
          <div className="rounded-2xl border border-white/10 bg-primary-900/70 backdrop-blur-sm p-6 sm:p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
            <p className="text-xs tracking-[0.18em] uppercase text-error-400 font-semibold mb-3">
              Unavailable
            </p>
            <h1 className="text-2xl sm:text-3xl font-semibold leading-tight mb-2">Access not configured</h1>
            <p className="text-white/70 text-sm sm:text-base">
              {pageLabel} is waiting on a valid deployment access code (12+ characters with a letter, number, and
              symbol). If you were sent a code and still see this, ask VOLOCEAN to confirm hosting configuration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isUnlocked) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-primary-950 text-white px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto mt-8 sm:mt-16">
        <div className="rounded-2xl border border-white/10 bg-primary-900/70 backdrop-blur-sm p-6 sm:p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
          <p className="text-xs tracking-[0.18em] uppercase text-warning-500 font-semibold mb-3">Access Required</p>
          <h1 className="text-2xl sm:text-3xl font-semibold leading-tight mb-2">Enter access code</h1>
          <p className="text-white/70 text-sm sm:text-base mb-6">Enter the shared code to view {pageLabel}.</p>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <label className="block">
              <span className="text-sm text-white/80">Access code</span>
              <input
                type="password"
                value={codeInput}
                onChange={(event) => setCodeInput(event.target.value)}
                className="mt-2 w-full rounded-xl border border-white/15 bg-primary-950/80 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-warning-500/70 focus:border-warning-500/70 transition-colors"
                placeholder="Enter your code"
                autoComplete="off"
                aria-label="Access code"
                aria-invalid={errorMessage ? true : undefined}
                aria-describedby={errorMessage ? 'standalone-code-gate-error' : undefined}
              />
            </label>

            {errorMessage && (
              <p id="standalone-code-gate-error" className="text-sm text-error-400" role="alert">
                {errorMessage}
              </p>
            )}

            <button
              type="submit"
              className="w-full rounded-xl bg-warning-500 text-primary-950 font-semibold px-4 py-3 hover:bg-warning-400 transition-colors focus:outline-none focus:ring-2 focus:ring-warning-500/70 focus:ring-offset-2 focus:ring-offset-primary-900"
            >
              Unlock page
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
